import React, { useState } from 'react';
import {
  Box, Button, Chip, DialogActions, DialogContent, DialogTitle, IconButton, Paper,
  Stack, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import AppDialog from '../../../shared/components/AppDialog';
import FileDropField from '../../../shared/components/FileDropField';

const MAX_EVIDENCE_FILES = 5;
const EVIDENCE_ACCEPT = '.pdf,.jpg,.jpeg,.png,.xlsx';

const evidenceName = evidence => evidence.name || evidence.file?.name || evidence.reference;
const formatEvidenceSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/** Capture FY evidence file metadata in memory without implying carbon verification. */
export default function EvidenceUploadList({
  evidenceRecords = [], fiscalYear, onAdd, onDownload, onRemove, readOnly = false,
  retirementMode = false, t,
}) {
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const remainingSlots = Math.max(MAX_EVIDENCE_FILES - evidenceRecords.length, 0);
  const addEvidenceFiles = (files = []) => {
    files.slice(0, remainingSlots).forEach((file, index) => onAdd?.({
      file,
      fiscalYear: Number(fiscalYear),
      id: `evidence-${Date.now()}-${evidenceRecords.length + index + 1}`,
      name: file.name,
      sizeBytes: file.size,
      status: 'ATTACHED',
      type: retirementMode ? 'RETIREMENT_CERTIFICATE' : 'SUPPORTING_DOCUMENT',
    }));
  };

  return (
    <Stack spacing={1.25}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
        <Chip
          label={t('climateAction.evidence.fileCountWithLimit', {
            count: evidenceRecords.length,
            defaultValue: `${evidenceRecords.length} / ${MAX_EVIDENCE_FILES} files`,
            limit: MAX_EVIDENCE_FILES,
          })}
          size={'small'} variant={'outlined'}
        />
      </Stack>
      {evidenceRecords.length > 0 ? (
        <Box sx={{
          display: 'grid', gap: 1,
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}>
          {evidenceRecords.map(evidence => (
            <Paper key={evidence.id} variant={'outlined'} sx={{ minWidth: 0, p: 1 }}>
              <Stack direction={'row'} alignItems={'center'} spacing={1.25}>
                <Box sx={{ color: 'primary.main', display: 'flex' }}><DescriptionOutlinedIcon /></Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 750 }} title={evidenceName(evidence)}>
                    {evidenceName(evidence)}
                  </Typography>
                  <Stack direction={'row'} spacing={0.75} useFlexGap flexWrap={'wrap'} sx={{ mt: 0.5 }}>
                    {formatEvidenceSize(evidence.sizeBytes ?? evidence.file?.size) && (
                      <Typography color={'text.secondary'} sx={{ fontSize: 12 }}>
                        {formatEvidenceSize(evidence.sizeBytes ?? evidence.file?.size)}
                      </Typography>
                    )}
                  </Stack>
                </Box>
                {evidence.id && !String(evidence.id).startsWith('evidence-') && onDownload && (
                  <Tooltip title={t('climateAction.evidence.download')}>
                    <IconButton onClick={() => onDownload(evidence)} size={'small'}>
                      <DownloadRoundedIcon fontSize={'small'} />
                    </IconButton>
                  </Tooltip>
                )}
                {!readOnly && (
                  <Tooltip title={t('climateAction.evidence.remove')}>
                    <IconButton onClick={() => setPendingRemoval(evidence)} size={'small'}>
                      <DeleteOutlineRoundedIcon fontSize={'small'} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Paper>
          ))}
        </Box>
      ) : readOnly ? (
        <Typography color={'text.secondary'} sx={{ fontSize: 14 }}>
          {t('climateAction.evidence.empty', { year: fiscalYear })}
        </Typography>
      ) : null}

      {!readOnly && (
        remainingSlots > 0 ? (
          <FileDropField
            accept={EVIDENCE_ACCEPT}
            acceptLabel={t('climateAction.evidence.acceptedFiles')}
            maxFiles={remainingSlots}
             maxSizeMb={10}
            multiple
            onChange={addEvidenceFiles}
          />
        ) : (
          <Typography color={'warning.main'} sx={{ fontSize: 12 }}>
            {t('climateAction.evidence.limit', { count: MAX_EVIDENCE_FILES })}
          </Typography>
        )
      )}

      <AppDialog
        fullWidth
        maxWidth="xs"
        onClose={() => setPendingRemoval(null)}
        open={Boolean(pendingRemoval)}
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 850, pr: 7 }}>
          {t('climateAction.evidence.removeTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14 }}>
            {t('climateAction.evidence.removeMessage', {
              name: pendingRemoval ? evidenceName(pendingRemoval) : '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setPendingRemoval(null)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            onClick={() => {
              onRemove?.(pendingRemoval.id);
              setPendingRemoval(null);
            }}
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </AppDialog>
    </Stack>
  );
}

export {
  EVIDENCE_ACCEPT, MAX_EVIDENCE_FILES, formatEvidenceSize,
};